import type { QueryParamConfig } from "use-query-params";

import {
  isJenkinsCustomEnv,
  isLocalHostMode,
  isUiStagingDomain,
} from "../../helpers";

/** `debugMode=1` — broad debug; `debugMode=2` — ride card map toolbox only. */
export const DEBUG_MODE_LEVEL_FULL = 1;
export const DEBUG_MODE_RIDE_CARD_MAP_TOOLBOX_LEVEL = 2;

/** Query key used by `use-query-params` for debug levels (`?debugMode=1`). */
export const DEBUG_MODE_QUERY_PARAM_KEY = "debugMode";

export const debugModeLevelQueryParam: QueryParamConfig<number | undefined> = {
  encode: (value: number | null | undefined): string | undefined =>
    value === null || value === undefined ? undefined : String(value),
  decode: (
    value: string | (string | null)[] | null | undefined,
  ): number | undefined => {
    const head = Array.isArray(value) ? value[0] : value;
    const s = head === null || head === undefined ? "" : String(head).trim();
    if (s === String(DEBUG_MODE_LEVEL_FULL)) {
      return DEBUG_MODE_LEVEL_FULL;
    }
    if (s === String(DEBUG_MODE_RIDE_CARD_MAP_TOOLBOX_LEVEL)) {
      return DEBUG_MODE_RIDE_CARD_MAP_TOOLBOX_LEVEL;
    }
    return undefined;
  },
};

export const resolveSearchStringForDebugModePresence = (
  locationSearch: string,
): string => {
  if (locationSearch) {
    return locationSearch;
  }
  if (typeof window === "undefined") {
    return "";
  }
  // Hash router (`#/login?emulateProd=1`) keeps query params in the hash, not `location.search`.
  const hash = window.location.hash;
  const qIndex = hash.indexOf("?");
  return qIndex === -1 ? "" : hash.slice(qIndex);
};

export const parseSearchHasDebugModeQueryParam = (search: string): boolean => {
  const normalized = search.startsWith("?") ? search.slice(1) : search;
  if (!normalized) {
    return false;
  }
  return new URLSearchParams(normalized).has(DEBUG_MODE_QUERY_PARAM_KEY);
};

export const hasEmulateProdQueryParam = (search = ""): boolean => {
  const resolvedSearch = resolveSearchStringForDebugModePresence(search);
  const normalized = resolvedSearch.startsWith("?")
    ? resolvedSearch.slice(1)
    : resolvedSearch;

  if (!normalized) {
    return false;
  }

  const emulateProdValue = new URLSearchParams(normalized).get("emulateProd");
  return Boolean(emulateProdValue);
};

export const getDebugModeLevelFromUrl = (search = ""): number | undefined => {
  const resolvedSearch = resolveSearchStringForDebugModePresence(search);
  const normalized = resolvedSearch.startsWith("?")
    ? resolvedSearch.slice(1)
    : resolvedSearch;

  if (!normalized) {
    return undefined;
  }

  return debugModeLevelQueryParam.decode(
    new URLSearchParams(normalized).get(DEBUG_MODE_QUERY_PARAM_KEY),
  );
};

export const isJenkinsTestPathname = (pathname: string): boolean => {
  return pathname.includes("-temp-web.");
};

/** Pure env/URL check for non-production navbar/login styling. Safe on routes without `QueryParamProvider`. */
export const isNonProductionNavbarColorFromEnv = (
  search = typeof window !== "undefined"
    ? window.location.search || window.location.hash
    : "",
): boolean => {
  // `?emulateProd=1` forces production palette on staging/local for visual comparison.
  if (hasEmulateProdQueryParam(search)) {
    return false;
  }

  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const debugModeLevel = getDebugModeLevelFromUrl(search);
  const debugMode = debugModeLevel !== undefined && debugModeLevel > 0;

  return (
    isLocalHostMode() ||
    debugMode ||
    isJenkinsTestPathname(pathname) ||
    isJenkinsCustomEnv() ||
    isUiStagingDomain()
  );
};
